import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { debugCheckAuth } from '../services/api';

function Debug() {
  const location = useLocation();
  const { currentUser, userProfile, error, refreshProfile } = useAuth();
  const [apiStatus, setApiStatus] = useState('Checking...');
  const [authTestResult, setAuthTestResult] = useState(null);
  const [authTestError, setAuthTestError] = useState(null);
  const [showFullToken, setShowFullToken] = useState(false);
  
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';
  
  useEffect(() => {
    const checkApi = async () => {
      try {
        // Just try to reach the API server without authentication
        const response = await axios.get(`${apiUrl}/health`);
        setApiStatus(`Connected - Status: ${response.status}`);
      } catch (err) {
        setApiStatus(`Error: ${err.message}`);
      }
    };
    
    checkApi();
  }, [apiUrl]);
  
  const testAuth = async () => {
    setAuthTestResult(null);
    setAuthTestError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setAuthTestError('No token found in localStorage');
        return;
      }
      
      const result = await debugCheckAuth(token);
      setAuthTestResult(result);
    } catch (err) {
      setAuthTestError(err.response?.data || err.message);
    }
  };

  const getStoredToken = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return 'No token found';
    return showFullToken ? token : `${token.substring(0, 20)}...`;
  };

  // Get all environment variables
  const getEnvVars = () => {
    const envVars = {};
    Object.keys(process.env).forEach(key => {
      envVars[key] = process.env[key];
    });
    return envVars;
  };
  
  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Debug Info</h1>
        <Link 
          to="/dashboard" 
          style={{
            padding: '6px 12px',
            textDecoration: 'none',
            color: '#333',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: '#f0f0f0'
          }}
        >
          Back to Dashboard
        </Link>
      </div>
      
      <h2>Application</h2>
      <div>
        <p><strong>Current Route:</strong> {location.pathname}</p>
        <p><strong>API URL:</strong> {apiUrl}</p>
        <p><strong>API Status:</strong> {apiStatus}</p>
        <p><strong>Environment:</strong> {process.env.NODE_ENV}</p>
        <p><strong>Client Version:</strong> {"0.2"}</p>
      </div>
      
      <h2>Environment Variables</h2>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '5px', overflowX: 'auto' }}>
          <pre>{JSON.stringify(getEnvVars(), null, 2)}</pre>
        </div>
      </div>
      
      <h2>Authentication</h2>
      <div>
        <p><strong>Is User Authenticated:</strong> {currentUser ? 'Yes' : 'No'}</p>
        {currentUser && (
          <>
            <p><strong>User ID:</strong> {currentUser.uid}</p>
            <p><strong>Email:</strong> {currentUser.email}</p>
            <p><strong>Email Verified:</strong> {currentUser.emailVerified ? 'Yes' : 'No'}</p>
            <p><strong>Provider ID:</strong> {currentUser.providerId}</p>
            <button onClick={async () => {
              try {
                const token = await currentUser.getIdToken(true);
                localStorage.setItem('authToken', token);
                alert('Token refreshed and stored in localStorage');
              } catch (err) {
                alert(`Error refreshing token: ${err.message}`);
              }
            }}>Refresh Token</button>
          </>
        )}
        
        <p><strong>User Profile Loaded:</strong> {userProfile ? 'Yes' : 'No'}</p>
        {userProfile && (
          <>
            <p><strong>Profile User ID:</strong> {userProfile.userId}</p>
            <p><strong>Team ID:</strong> {userProfile.teamId || 'NOT SET!'}</p>
            <p><strong>Team Name:</strong> {userProfile.teamName || 'NOT SET!'}</p>
            <div style={{ marginTop: '10px' }}>
              {refreshProfile && (
                <button 
                  onClick={() => {
                    refreshProfile().then(() => alert('Profile refreshed')).catch(e => alert('Error: ' + e.message));
                  }}
                  style={{ marginRight: '10px' }}
                >
                  Refresh Profile
                </button>
              )}
              
              {currentUser && (
                <button 
                  onClick={async () => {
                    try {
                      // Force a direct API call to /user/me with fresh token to trigger auto-provisioning
                      const token = await currentUser.getIdToken(true);
                      const response = await axios.get(`${apiUrl}/user/me`, {
                        headers: { 
                          Authorization: `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      console.log('Manual team check response:', response.data);
                      alert('Manual user profile fetch completed. Check console for details.');
                      
                      // Refresh the profile in context if the function exists
                      if (refreshProfile) {
                        await refreshProfile();
                      }
                    } catch (err) {
                      console.error('Manual team check error:', err);
                      alert(`Error: ${err.message}`);
                    }
                  }}
                >
                  Force Team Creation
                </button>
              )}
            </div>
            <pre style={{ marginTop: '10px', padding: '10px', background: '#f5f5f5', overflowX: 'auto' }}>
              {JSON.stringify(userProfile, null, 2)}
            </pre>
          </>
        )}
        
        {error && (
          <p style={{ color: 'red' }}><strong>Error:</strong> {error}</p>
        )}
      </div>
      
      <h2>Auth Token</h2>
      <div>
        <p>
          <strong>Token:</strong> {getStoredToken()}
          <button 
            onClick={() => setShowFullToken(!showFullToken)}
            style={{ marginLeft: '10px' }}
          >
            {showFullToken ? 'Hide' : 'Show Full Token'}
          </button>
        </p>
        <button onClick={testAuth}>Test Auth Token</button>
        
        {authTestResult && (
          <div style={{ marginTop: '10px', padding: '10px', background: '#e6ffe6', borderRadius: '5px' }}>
            <h4>Auth Test Success</h4>
            <pre>{JSON.stringify(authTestResult, null, 2)}</pre>
          </div>
        )}
        
        {authTestError && (
          <div style={{ marginTop: '10px', padding: '10px', background: '#ffe6e6', borderRadius: '5px' }}>
            <h4>Auth Test Error</h4>
            <pre>{JSON.stringify(authTestError, null, 2)}</pre>
          </div>
        )}
      </div>
      
      <h2>Local Storage</h2>
      <div>
        {Object.keys(localStorage).length > 0 ? (
          Object.keys(localStorage).map(key => (
            <p key={key}>
              <strong>{key}:</strong> {
                key === 'authToken' 
                  ? `${localStorage.getItem(key).substring(0, 10)}...` 
                  : localStorage.getItem(key)
              }
            </p>
          ))
        ) : (
          <p>No items in localStorage</p>
        )}
      </div>

      <h2>CORS Test</h2>
      <div>
        <button onClick={async () => {
          try {
            const response = await fetch(`${apiUrl}/health`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            });
            alert(`CORS Test Result: ${response.status} ${response.statusText}`);
          } catch (err) {
            alert(`CORS Test Error: ${err.message}`);
          }
        }}>Test CORS</button>
      </div>
    </div>
  );
}

export default Debug;